import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApplicationConfig } from '../config/config';
import { UrlUtil } from '../config/urlutil';
import {
  CountryLanguages,
  LanguageDetails
} from '../model/country-language.model';
import { CookieHelper } from './cookie-helper';

@Injectable({
  providedIn: 'root'
})
export class CountryLanguageHelper {
  public prefCookie = '';
  private countryLanguage: BehaviorSubject<string>;
  private countryLanguageObservables: Observable<any>;
  private countryLanguageList;
  private translatedText;
  private regionList;
  private redirectionList;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(CookieHelper) private cookieHelper: CookieHelper,
    private appconfig: ApplicationConfig,
    private urlUtil: UrlUtil
  ) {
    if (isPlatformBrowser(this.platformId)) {
      const pref = this.cookieHelper.getCookies('prefUI');
      if (pref && pref !== '') {
        const res = pref.split('=');
        if (res[1]) {
          this.prefCookie = res[1];
        }
      }
    }
  }

  getPreferedLangagues(): Observable<string> {
    if (!this.countryLanguageObservables) {
      if (!this.countryLanguage) {
        this.countryLanguage = new BehaviorSubject<string>(
          this.prefCookie ? this.prefCookie : ''
        );
      }
      this.countryLanguageObservables = this.countryLanguage.asObservable();
    }
    return this.countryLanguageObservables;
  }

  setPreferedLanguages(prefvalue: string) {
    this.prefCookie = prefvalue;
    if (this.countryLanguage) {
      this.countryLanguage.next(prefvalue);
    }
  }

  setPrefCookie(prefvalue: string) {
    if (isPlatformBrowser(this.platformId)) {
      this.cookieHelper.setCookies('prefUI', prefvalue);
    }
  }

  setShowConfirmationModal(shouldShow: boolean) {
    if (isPlatformBrowser(this.platformId)) {
      if (shouldShow) {
        this.cookieHelper.setCookies('prefConf', 'Y');
      } else {
        this.cookieHelper.setCookies('prefConf', 'N');
      }
    }
  }

  shouldShowConfirmationModal() {
    if (isPlatformBrowser(this.platformId)) {
      const str = this.cookieHelper.getCookies('prefConf');
      if (str && str !== '') {
        const res = str.split('=');
        if (res[1] === 'Y') {
          return true;
        } else {
          return false;
        }
      }
      return false;
    }
  }

  getCountryForCountryCode(countryCode: string) {
    countryCode = countryCode.toLowerCase();
    const languageList = this.countryLanguageList.filter(function (obj) {
      return obj.isoCode.toLowerCase().indexOf(countryCode) !== -1;
    });
    if (languageList.length > 0) {
      return languageList[0];
    }
    return null;
  }

  getLangaugeForLanguageCode(countryCode: string, languageCode: string) {
    countryCode = countryCode.toLowerCase();
    languageCode = languageCode.toLowerCase();
    if (this.countryLanguageList) {
      const country = this.getCountryForCountryCode(countryCode);
      if (country) {
        const language = country.languageDtls.filter((item) => {
          return item.languageCode === languageCode;
        });
        if (language && language.length > 0) {
          return language[0];
        }
      }
    }
  }

  getPrimaryLanguage(countryCode: string) {
    if (this.countryLanguageList) {
      countryCode = countryCode.toLowerCase();
      const languageList = this.getCountryForCountryCode(countryCode);
      if (languageList) {
        const prefLanguage = languageList.languageDtls.filter((item) => {
          return item.prefLanguage === 'Y';
        });
        if (prefLanguage.length > 0) {
          return prefLanguage[0];
        }
      }
    }
    return null;
  }

  getSecondaryLanguage(countryCode: string) {
    if (this.countryLanguageList) {
      countryCode = countryCode.toLowerCase();
      const languageList = this.countryLanguageList.filter((item) => {
        return item.isoCode.toLowerCase() === countryCode;
      });
      if (languageList.length > 0) {
        const secondaryPrefLanguage = languageList[0].languageDtls.filter(
          (item) => {
            if (item.secondaryPrefLanguage) {
              return item.secondaryPrefLanguage === 'Y';
            }
          }
        );
        if (secondaryPrefLanguage.length > 0) {
          return secondaryPrefLanguage[0];
        }
      }
    }
    return null;
  }

  getSecondaryLanguageForSelectedLanguage(
    countryCode: string,
    primarylanguage: string
  ) {
    if (this.countryLanguageList) {
      countryCode = countryCode.toLowerCase();
      primarylanguage = primarylanguage.toLowerCase();
      const languageList = this.countryLanguageList.filter((item) => {
        return item.isoCode.toLowerCase() === countryCode;
      });
      if (languageList.length > 0) {
        const secondaryPrefLanguage = languageList[0].languageDtls.filter(
          (item) => {
            return (
              item.languageCode.toLowerCase() !== primarylanguage &&
              ((item.secondaryPrefLanguage
                ? item.secondaryPrefLanguage === 'Y'
                : false) ||
                item.prefLanguage === 'Y')
            );
          }
        );
        if (secondaryPrefLanguage.length > 0) {
          return secondaryPrefLanguage[0];
        }
      }
    }
    return null;
  }

  getTranslatedTextForLanguageCode(languagecode: string, textkey: string) {
    if (this.translatedText && languagecode) {
      languagecode = languagecode.toLowerCase();
      const translatedmsg = this.translatedText[languagecode];
      if (translatedmsg) {
        return translatedmsg[textkey];
      }
    }
    return null;
  }

  // getTranslatedTextForLanguageCode(languagecode: string, textkey: string) {
  //   if (this.translatedText && languagecode) {
  //     languagecode = languagecode.toLowerCase();
  //     let translatedmsg = this.translatedText.filter(item => {
  //       return item.languageCode.toLowerCase() === languagecode;;
  //     });
  //     if (translatedmsg && translatedmsg.length > 0)
  //       return translatedmsg[0][textkey];
  //   }
  //   return null;
  // }

  checkDomainForLangauge(domainName, languagecode) {
    languagecode = languagecode.toLowerCase();
    if (languagecode === 'en') {
      return true;
    } else {
      const domain = domainName.split('.');
      return domain[0].contains(languagecode);
    }
  }

  setData(countryLanguageData) {
    this.countryLanguageList = countryLanguageData['countryLanguages'];
    this.regionList = countryLanguageData['regionlist'];
    this.translatedText = countryLanguageData['translated-text'];
    this.redirectionList = countryLanguageData['redirectionUrl'];
  }

  getCountryLanguageList() {
    return this.countryLanguageList;
  }

  getRegionList() {
    return this.regionList;
  }

  getTranslatedTextList() {
    return this.translatedText;
  }

  deleteCookie(prefUI) {
    const date = new Date();
    // Set it expire in -1 days
    date.setTime(date.getTime() + -1 * 24 * 60 * 60 * 1000);
    // Set it
    document.cookie =
      'prefUI' +
      '=; expires=' +
      date.toUTCString() +
      ';domain=.delta.com; path=/';

    const cookiesLife = new Date();
    cookiesLife.setTime(cookiesLife.getTime() + 365 * 24 * 60 * 60 * 1000);
    const expires = '; expires=' + cookiesLife.toUTCString();
    const cookieDomain = ';domain=.delta.com; path=/';
    const cookieDetails = prefUI + '=' + 'en-us' + expires + cookieDomain;
    document.cookie = cookieDetails;
  }

  getCookieValue() {
    if (this.countryLanguageList) {
      const url = window.location.host;
      const countryCode = this.getCountryCodeFromPrefCookie();

      let languageCode = '';
      let prefUI = 'en-us';

      const languageList = this.countryLanguageList.filter((item) => {
        if (item.isoCode === countryCode && !languageCode) {
          languageCode = this.getLangCode(item, url);
        }
      });
      if (languageCode) {
        prefUI = languageCode + '-' + countryCode;
      }
      this.deleteCookie(prefUI);
    }
  }

  getLangCode(item, url) {
    const itemLang = item.languageDtls.filter((languageURL) => {
      if (languageURL.url.toLowerCase() === url) {
        return languageURL.languageCode;
      }
    });

    return itemLang[0].languageCode;
  }

  checkForKey(currentURl, search) {
    if (currentURl.indexOf(search) > -1) {
      return true;
    } else {
      return false;
    }
  }

  getRedirectionValue(item, currentPath) {
    let response = null;
    const searchKey = item['searchKey'];
    if (searchKey) {
      const isKey = this.checkForKey(currentPath, searchKey);
      if (isKey) {
        response = item.url;
      }
    } else {
      response = item.url;
    }
    return response;
  }

  isStopRedirection() {
    const currentPath = window.location.pathname;
    const responseValue = this.redirectionList.find((item) => {
      if (item.url.toLowerCase() === currentPath.toLowerCase()) {
        return this.getRedirectionValue(item, currentPath);
      } else {
        return null;
      }
    });
    let isKeyPresent = false;
    if (responseValue) {
      isKeyPresent = true;
    }
    return isKeyPresent;
  }

  redirectToTranslatedSites(country: string, language: string) {
    if (this.isStopRedirection()) {
      this.getCookieValue();
    } else {
      const appDomain = this.urlUtil.baseUrl;
      const prdENV = this.getValidDomainName(appDomain); // TODO: Add proper typing
      const defaultCountry = this.appconfig.get('APP_DEFAULT_COUNTRY'); // TODO: Use injectable token
      let actionUrl = ''; // TODO: Add proper typing
      let urlToRedirect = ''; // TODO: Add proper typing
      if (this.countryLanguageList && country) {
        country = country.toLowerCase(); // TODO: Add null check for country
        const languageList = this.countryLanguageList.filter((item) => {
          return item.isoCode.toLowerCase() === country; // TODO: Add null check for item.isoCode
        });
        language = language.toLowerCase();
        if (
          languageList.length > 0 &&
          languageList[0] &&
          languageList[0].languageDtls
        ) {
          const languageListItem = languageList[0];
          const reqdLanguage = languageListItem.languageDtls.filter((item) => {
            return item.languageCode === language; // TODO: Add null check for item.languageCode
          });
          urlToRedirect = this.getUrlToRedirectForCDC();
          if (reqdLanguage.length > 0 && reqdLanguage[0]) {
            const requiredFiteredLanguage = reqdLanguage[0];
            if (languageListItem.urlcc) {
              if (
                languageListItem.urlcc === defaultCountry &&
                urlToRedirect === ''
              ) {
                actionUrl = requiredFiteredLanguage.url; // TODO: Add null check for requiredFiteredLanguage and requiredFiteredLanguage.url
              } else {
                actionUrl = `${requiredFiteredLanguage.url}/${languageListItem.urlcc}/${requiredFiteredLanguage.languageCode}`;
                // TODO: Add null check for anguageListItem.urlcc and requiredFiteredLanguage.languageCode
              }
            } else {
              actionUrl = requiredFiteredLanguage.url;
            }
          }
        }
      }
      // Do Not redirect if current url = the redirection url
      let currentUrl;
      const res = appDomain.split('//');
      if (res.length > 1) {
        currentUrl = res[1];
      } else {
        currentUrl = res[0];
      }

      if (
        prdENV === 'localhost' ||
        actionUrl === '' ||
        currentUrl === actionUrl
      ) {
        return;
      }
      urlToRedirect = urlToRedirect ? urlToRedirect : '';
      window.location.href = `${window.location.protocol}//${actionUrl}${urlToRedirect}`;
    }
  }

  // code for cdc
  getUrlToRedirectForCDC(): string {
    if (isPlatformBrowser(this.platformId)) {
      if (this.redirectionList && this.redirectionList.length > 0) {
        const currentLocationUrl = window.location.href;
        let urlToRedirect = ''; // TODO: Add proper typing
        this.redirectionList.filter((item) => {
          if (
            item.key &&
            currentLocationUrl &&
            currentLocationUrl.indexOf(item.key) !== -1
          ) {
            urlToRedirect = item.url;
          }
        });
        return urlToRedirect;
      }
    }
  }

  redirectToTranslatedSitesForContentPages(country: string, language: string) {
    const appDomain = this.urlUtil.baseUrl;
    const prdENV = this.getValidDomainName(appDomain);
    let actionUrl = ''; // TODO: Add proper typing
    let languageList;
    let reqdLanguage;

    if (this.countryLanguageList) {
      country = country.toLowerCase();
      languageList = this.countryLanguageList.filter((item) => {
        return item.isoCode.toLowerCase() === country;
      });
      language = language.toLowerCase();
      if (languageList.length > 0) {
        reqdLanguage = languageList[0].languageDtls.filter((item) => {
          return item.languageCode === language;
        });
        if (reqdLanguage.length > 0) {
          actionUrl = reqdLanguage[0].url;
        }
      }
    }

    const currentPage = window.location.href;

    let startIndex = currentPage.indexOf('.com/') + 4;
    const args = currentPage.split('/');
    const countryCode = args[3];
    const languageCode = args[4] ? args[4] : '';
    const returnCode = args[5] ? args[5] : '';
    /** Commented the if condition for Key 208 validation */
    // if (this.isLanguageSupportedByCountry(countryCode, languageCode)) {
    if (reqdLanguage.length > 0) {
      if (languageList[0].urlcc && languageList[0].urlcc !== '') {
        actionUrl =
          reqdLanguage[0].url +
          '/' +
          languageList[0].urlcc +
          '/' +
          reqdLanguage[0].languageCode; // TODO: Use template literal instaed of string concat
      } else {
        actionUrl = reqdLanguage[0].url;
      }
    }
    startIndex += 2 + countryCode.length + languageCode.length;
    // }

    if (startIndex > 3) {
      let currentPath;
      if (returnCode) {
        currentPath = currentPage.substring(currentPage.length);
      } else {
        currentPath = currentPage.substring(startIndex);
      }
      if (!(window.location.hostname === 'localhost')) {
        actionUrl += currentPath;
      }
    }
    window.location.href = window.location.protocol + '//' + actionUrl;
  }

  getValidDomainName(domainHost: string) {
    const res = domainHost.split('//');
    if (res.length > 1) {
      domainHost = res[1];
    } else {
      domainHost = res[0];
    }
    // const re = new RegExp(/www\d*|delta|draft\d*|dvl\d*|si\d*|st\d*|qa\d*|ddwa\d*|dswa\d*|dtwa\d*|dpwa\d*|localhost|prd\d*|\d*|.*as\d*|author|publish/);
    const re = new RegExp(
      /www\d*|delta|draft\d*|dvl\d*|si\d*|st\d*|qa\d*|ddwa\d*|dswa\d*|dtwa\d*|dpwa\d*|localhost|prd\d*|author\d*|publish\d*|preview\d*|\d*|.*as\d*/
    );
    return domainHost.match(re)[0] === 'www' ? '' : domainHost.match(re)[0];
  }

  getDomainName(domainHost: string) {
    const res = domainHost.split('//');
    if (res.length > 1) {
      domainHost = res[1];
    } else {
      domainHost = res[0];
    }
    if (domainHost) {
      domainHost = domainHost.split('.')[0];
    }
    return domainHost;
  }

  isDomainCorrectForPrefCookie() {
    try {
      const appDomain = this.urlUtil.baseUrl;
      const domain = this.getDomainName(appDomain);
      const languageCode = this.getLanguageCodeFromPrefCookie();
      if (
        ((domain === 'www' ||
          domain.includes('qat') ||
          domain === 'si' ||
          domain === 'st' ||
          domain.includes('dev1') ||
          domain.includes('stg') ||
          domain.includes('publish') ||
          domain.includes('author') ||
          domain.includes('preview') ||
          domain.includes('st1') ||
          domain.includes('st2') ||
          domain.includes('st3') ||
          domain.includes('prd')) &&
          languageCode === 'en') ||
        domain === languageCode
      ) {
        return true;
      } else {
        const valid = this.getValidDomainName(appDomain);
        // Skip localhost && dvl redirection
        if (
          valid &&
          (valid === 'localhost' ||
            valid.includes('dvl') ||
            valid.includes('publish') ||
            valid.includes('author') ||
            valid.includes('preview'))
        ) {
          return true;
        }
        if (valid && valid !== '') {
          const url = domain.split('-');
          if (url.length > 1) {
            if (url[1] === languageCode) {
              return true;
            }
          }
        }
      }
      return false;
    } catch (err) {
      return true;
    }
  }

  isDomainCorrectForLanguageCode(languageCode: string) {
    try {
      const appDomain = this.urlUtil.baseUrl;
      const domain = this.getDomainName(appDomain);
      if (
        ((domain === 'www' ||
          domain.includes('qat') ||
          domain === 'si' ||
          domain === 'st' ||
          domain.includes('dev1') ||
          domain.includes('stg') ||
          domain.includes('st1') ||
          domain.includes('st2') ||
          domain.includes('st3') ||
          domain.includes('prd')) &&
          languageCode === 'en') ||
        domain === languageCode
      ) {
        return true;
      } else {
        const valid = this.getValidDomainName(appDomain);
        // Skip localhost && dvl redirection
        if (valid && (valid === 'localhost' || valid.includes('dvl'))) {
          return true;
        }
        if (valid && valid !== '') {
          const url = domain.split('-');
          if (url.length > 1) {
            if (url[1] === languageCode) {
              return true;
            }
          }
        }
      }
      return false;
    } catch (err) {
      return true;
    }
  }

  getCountryCodeFromPrefCookie() {
    const prefCookie = this.cookieHelper.getCookies('prefUI');
    if (prefCookie) {
      if (prefCookie.length > 0) {
        let res = prefCookie.split('=');
        res = res[1].split('-');
        return res[1].toLowerCase();
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  getLanguageCodeFromPrefCookie() {
    const prefCookie = this.cookieHelper.getCookies('prefUI');
    if (prefCookie) {
      if (prefCookie.length > 0) {
        let res = prefCookie.split('=');
        res = res[1].split('-');
        return res[0].toLowerCase();
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  getRegtionForCountryCode(countryCode: string) {
    countryCode = countryCode.toLowerCase();
    if (this.regionList) {
      const country = this.getCountryForCountryCode(countryCode);
      if (country) {
        for (const item of this.regionList) {
          for (const regionCode of item.region_codes) {
            if (country.regionCode === regionCode) {
              return item;
            }
          }
        }
      }
    }
    return null;
  }

  detectBrowserLanguage() {
    if (isPlatformBrowser(this.platformId)) {
      const lan = window.navigator.languages;
      if (lan && lan.length > 0) {
        const res = lan[0].split('-');
        return res[0];
      } else {
        // IE
        const lang2 = window.navigator.language;
        if (lang2) {
          const res = lang2.split('-');
          return res[0];
        }
      }
    }
    return null;
  }

  isLanguageSupportedByCountry(countryCode: string, languageCode: string) {
    countryCode = countryCode.toLowerCase();
    languageCode = languageCode.toLowerCase();
    if (this.countryLanguageList && languageCode && languageCode !== '') {
      const country = this.getCountryForCountryCode(countryCode);
      if (country && country.languageDtls && country.languageDtls.length > 0) {
        const language = country.languageDtls.filter((item) => {
          return item.languageCode === languageCode;
        });
        if (language.length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  loadSelectedLanguage(
    countryCode: string,
    languageCode: string,
    enableLanguageSelectionModal: string
  ): void {
    if (
      isPlatformBrowser(this.platformId) &&
      enableLanguageSelectionModal !== 'true'
    ) {
      return;
    }

    // Cache country language list to avoid race condition
    const countryLanguageList = this.getCountryLanguageList();
    if (!countryLanguageList?.length) return;

    const pathNameList = location.pathname.split('/').filter(Boolean);

    if (pathNameList.length >= 2) {
      const currentCountryCode = pathNameList[0];
      const currentLanguageCode = pathNameList[1];

      // Single call to findLanguageDetails to avoid performance issues
      const targetResult = this.findLanguageDetails(
        countryCode,
        languageCode,
        'isoCode',
        countryLanguageList
      );
      const currentResult = this.findLanguageDetails(
        currentCountryCode,
        currentLanguageCode,
        'urlcc',
        countryLanguageList
      );

      if (currentResult && targetResult) {
        const url = new URL(location.href);
        url.pathname = url.pathname.replace(
          `/${currentCountryCode}/${currentLanguageCode}`,
          `/${targetResult.countryMatch.urlcc}/${languageCode}`
        );
        url.hostname = targetResult.languageDetails.url;
        location.href = url.toString();
        return;
      }
    }

    // Fallback: prefUI cookie
    const prefUICookie = this.cookieHelper.getCookieValue('prefUI');
    if (prefUICookie?.length) {
      const cookieParts = prefUICookie.split('-');
      if (cookieParts.length >= 2) {
        const [prefLang, prefCountry] = cookieParts;
        const result = this.findLanguageDetails(
          prefCountry,
          prefLang,
          'isoCode',
          countryLanguageList
        );

        if (result) {
          const url = new URL(location.href);
          const pathName = `/${result.countryMatch.urlcc}/${prefLang}`;
          url.hostname = result.languageDetails.url;

          if (
            location.pathname === '/' &&
            pathName !== '/us/en' &&
            pathName !== '/us/es'
          ) {
            url.pathname = pathName;
          }

          location.href = url.toString();
        }
      }
    }
  }

  private findLanguageDetails(
    countryCode: string,
    languageCode: string,
    countryKey: string,
    countryLanguageList?: CountryLanguages[]
  ): {
    countryMatch: CountryLanguages;
    languageDetails: LanguageDetails;
  } | null {
    if (!countryCode || !languageCode || !countryKey) {
      return null;
    }

    // Use provided list or get from instance to avoid race conditions
    const list = countryLanguageList || this.getCountryLanguageList();
    if (!list?.length) {
      return null;
    }

    const countryMatch = list.find(
      (country) => country?.[countryKey] === countryCode
    );
    if (!countryMatch?.languageDtls?.length) {
      return null;
    }

    const languageDetails = countryMatch.languageDtls.find(
      (language) => language?.languageCode === languageCode
    );

    return languageDetails ? { countryMatch, languageDetails } : null;
  }
}
